<template>
  <v-dialog v-model="dialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left>mdi-pencil</v-icon>
        {{ isNew ? 'Add Product' : 'Edit Product' }}
      </v-card-title>

      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid">
          <v-text-field
            v-model="editedProduct.name"
            label="Product Name"
            :rules="[v => !!v || 'Product name is required']"
            required
            outlined
          ></v-text-field>

          <!-- Image Upload -->
          <v-card outlined class="mt-4 pa-4">
            <v-card-title class="subtitle-2">Product Image</v-card-title>
            
            <!-- File Upload -->
            <v-file-input
              v-model="imageFile"
              accept="image/*"
              label="Upload Image"
              prepend-icon="mdi-camera"
              @change="handleImageUpload"
              :rules="[v => !v || v.size < 5000000 || 'Image size should be less than 5 MB']"
              show-size
              counter
              outlined
              class="mb-4"
            ></v-file-input>

            <!-- Image Preview -->
            <div v-if="editedProduct.image" class="text-center">
              <v-img 
                :src="editedProduct.image" 
                height="150" 
                contain
                class="mb-2 transparent-bg"
                :class="{ 'error-image': !imageValid }"
              ></v-img>
              <p v-if="!imageValid" class="caption text-error">Image not found</p>
              <v-btn 
                small 
                text 
                color="error" 
                @click="removeImage"
                class="mt-2"
              >
                <v-icon left small>mdi-delete</v-icon>
                Remove Image
              </v-btn>
            </div>

            <!-- Upload Progress -->
            <v-progress-linear
              v-if="uploading"
              :value="uploadProgress"
              color="primary"
              height="20"
              class="mt-2"
            >
              <template v-slot:default="{ value }">
                <strong>{{ Math.ceil(value) }}%</strong>
              </template>
            </v-progress-linear>
          </v-card>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn 
          color="primary" 
          @click="save" 
          :disabled="!valid"
          :loading="saving"
        >
          {{ isNew ? 'Add' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import config from '@/config.js'

export default {
  name: 'EditProductDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      valid: false,
      saving: false,
      editedProduct: {},
      imageValid: true,
      imageFile: null,
      uploading: false,
      uploadProgress: 0
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    isNew() {
      return !this.product || !this.product._id
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.editedProduct = { ...this.product }
        this.checkImage()
      }
    },
    'editedProduct.image'() {
      this.checkImage()
    }
  },
  methods: {
    async checkImage() {
      if (this.editedProduct.image) {
        try {
          const img = new Image()
          img.onload = () => {
            this.imageValid = true
          }
          img.onerror = () => {
            // If it's an old file system URL, try to convert it
            if (this.editedProduct.image.includes('/uploads/')) {
              this.convertOldImageUrl(this.editedProduct.image)
            } else {
              this.imageValid = false
            }
          }
          img.src = this.editedProduct.image
        } catch (error) {
          this.imageValid = false
        }
      }
    },
    
    async convertOldImageUrl(oldUrl) {
      try {
        // Try to fetch the old image and convert to base64
        const response = await fetch(oldUrl)
        if (response.ok) {
          const blob = await response.blob()
          const reader = new FileReader()
          reader.onload = () => {
            this.editedProduct.image = reader.result
            this.imageValid = true
          }
          reader.readAsDataURL(blob)
        } else {
          this.imageValid = false
        }
      } catch (error) {
        this.imageValid = false
      }
    },
    async handleImageUpload(file) {
      if (!file) return;
      
      this.uploading = true;
      this.uploadProgress = 0;
      
      try {
        const formData = new FormData();
        formData.append('image', file);
        
        const response = await fetch(`${config.API_BASE_URL}/upload/image`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Upload failed');
        }
        
        const result = await response.json();
        
        if (result.success) {
          // Update the image URL with the Cloudinary URL
          this.editedProduct.image = result.imageUrl;
          this.imageValid = true;
          this.$emit('image-uploaded', result.imageUrl);
          
          // Image optimized successfully
        } else {
          throw new Error(result.message || 'Upload failed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.$emit('upload-error', error.message);
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
        this.imageFile = null;
      }
    },
    
    removeImage() {
      this.editedProduct.image = '';
      this.imageValid = false;
      this.imageFile = null;
    },
    
    async save() {
      if (this.$refs.form.validate()) {
        this.saving = true
        try {
          await this.$emit('save', { ...this.editedProduct })
        } finally {
          this.saving = false
        }
      }
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.error-image {
  opacity: 0.3;
  border: 2px dashed #ff5252;
}

.transparent-bg {
  background: transparent !important;
}

.transparent-bg .v-image__image {
  background: transparent !important;
}

.transparent-bg .v-image__image--cover {
  background: transparent !important;
}

.transparent-bg .v-image__image--contain {
  background: transparent !important;
}
</style> 